import { call, put, takeLatest, select } from 'redux-saga/effects';
import { callWithAuth } from '../sagaApiHelper';
import { ensureWhatsAppModuleAccess } from './helpers/whatsAppAccessHelper';
import { getConversationApi, listConversationsApi, sendMessageApi, closeConversationApi } from '../../api/apiLists/conversationApi';
import { createConversationApi } from '../../api/apiLists/createConversationApi';
import { uploadTemplateMediaApi } from '../../api/apiLists/uploadTemplateMediaApi';
import { uploadFileToS3 } from './fileUploadHelper';
import { getOrFetchWabaId } from './whatsapp/utils';
import { showAlert } from '../alertSlice';
import {
  fetchConversationsRequest,
  fetchConversationsSuccess,
  fetchConversationsError,
  fetchConversationRequest,
  fetchConversationSuccess,
  fetchConversationError,
  markConversationAsRead,
  sendMessageSuccess,
  sendMessageError,
  createConversationRequest,
  createConversationSuccess,
  createConversationError,
  closeConversationRequest,
  closeConversationSuccess,
  closeConversationError,
} from '../conversationSlice';
import {
  transformApiResponse,
  transformConversationsListResponse,
  transformMessage,
} from '../../components/whatsapp/chat/conversationTransform';

function* handleFetchConversations(action) {
  if (!(yield call(ensureWhatsAppModuleAccess))) {
    return;
  }

  try {
    const { status = 'OPEN', limit = 10, nextKey = null } = action.payload || {};
    const res = yield call(callWithAuth, listConversationsApi, {
      status,
      limit,
      nextKey,
    });

    if (res?.status === true) {
      const transformedData = yield call(
        transformConversationsListResponse,
        res.data
      );
      yield put(fetchConversationsSuccess(transformedData));
      return;
    }

    const errorMessage = res?.errMsg || 'Failed to fetch conversations';
    yield put(fetchConversationsError(errorMessage));
    yield put(
      showAlert({
        message: errorMessage,
        type: 'error',
      })
    );
  } catch (error) {
    const errorMessage = error?.message || 'Error fetching conversations';
    yield put(fetchConversationsError(errorMessage));
    yield put(
      showAlert({
        message: errorMessage,
        type: 'error',
      })
    );
  }
}

function* handleFetchConversation(action) {
  if (!(yield call(ensureWhatsAppModuleAccess))) {
    return;
  }

  try {
    const { conversationId } = action.payload || {};


    if (!conversationId) {
      yield put(
        fetchConversationError('Conversation ID is required')
      );
      yield put(
        showAlert({
          message: 'Invalid conversation ID',
          type: 'error',
        })
      );
      return;
    }

    const res = yield call(callWithAuth, getConversationApi, {
      conversationId,
    });

    if (res?.status === true) {
      const transformedData = yield call(
        transformApiResponse,
        res.data
      );
      yield put(fetchConversationSuccess(transformedData));
      yield put(markConversationAsRead(transformedData?.conversation?.id || conversationId));
    } else {
      const errorMessage = res?.errMsg || 'Failed to fetch conversation';
      yield put(fetchConversationError(errorMessage));
      yield put(
        showAlert({
          message: errorMessage,
          type: 'error',
        })
      );
    }
  } catch (error) {
    console.error('Error fetching conversation:', error);
    const errorMessage = error?.message || 'Error fetching conversation';
    yield put(fetchConversationError(errorMessage));
    yield put(
      showAlert({
        message: errorMessage,
        type: 'error',
      })
    );
  }
}

function* handleSendMessage(action) {
  if (!(yield call(ensureWhatsAppModuleAccess))) {
    return;
  }

  try {
    const { conversationId, contentType = 'TEXT', contentData = {}, messageMetadata = {} } = action.payload || {};

    if (!conversationId) {
      yield put(
        sendMessageError('Conversation ID is required')
      );
      yield put(
        showAlert({
          message: 'Invalid conversation ID',
          type: 'error',
        })
      );
      return;
    }

    if (!contentType) {
      yield put(
        sendMessageError('Content type is required')
      );
      yield put(
        showAlert({
          message: 'Content type is required',
          type: 'error',
        })
      );
      return;
    }

    // --- Template media upload interception ---
    let finalContentData = { ...contentData };

    if (contentType === 'TEMPLATE' && contentData?.template?.mediaFile) {
      const mediaFileObj = contentData.template.mediaFile;
      const actualFile = mediaFileObj.file;
      const passedCompanyId = contentData.template.companyId;

      // Step 1: Upload file to S3 (same pattern as mediaUploadSaga)
      let s3Key = null;
      try {
        s3Key = yield call(uploadFileToS3, actualFile, passedCompanyId, 'chat-service', { conversationId });
      } catch (uploadError) {
        const errorMsg = uploadError?.message || 'Failed to upload media file';
        yield put(showAlert({ message: errorMsg, type: 'error' }));
        yield put(sendMessageError(errorMsg));
        return;
      }

      if (!s3Key) {
        yield put(showAlert({ message: 'Failed to upload media', type: 'error' }));
        yield put(sendMessageError('Media upload failed'));
        return;
      }

      // Parse s3Key into bucket and key (same pattern as campaignSaga)
      let mediaBucket = '';
      let mediaKey = s3Key;
      if (typeof s3Key === 'string' && s3Key.startsWith('s3://')) {
        const parts = s3Key.replace('s3://', '').split('/');
        mediaBucket = parts.shift();
        mediaKey = parts.join('/');
      }

      // Step 2: Register media with WhatsApp via uploadTemplateMediaApi
      const companyInfo = yield select(state => state.companyInfo?.info || {});
      const selectedPhone = yield select(state => state.whatsapp?.selectedPhone || {});
      const wabaIdFromStorage = yield call(getOrFetchWabaId);
      const wabaId = wabaIdFromStorage || companyInfo?.wabaId || companyInfo?.whatsappBusinessAccountId || '';

      const activeConversation = yield select(state => state.conversation?.activeConversation || state.conversation?.currentConversation);
      const displayPhoneNumberId = activeConversation?.channelId || selectedPhone?.id;

      const mediaUploadPayload = {
        wabaId: activeConversation?.channelAccountId || wabaId,
        displayPhoneNumberId,
        mimeType: mediaFileObj.type,
        source: 's3',
        bucket: mediaBucket,
        key: mediaKey
      };

      const mediaUploadRes = yield call(callWithAuth, uploadTemplateMediaApi, mediaUploadPayload);
      let mediaId = null;

      if (mediaUploadRes?.status === true && mediaUploadRes?.data?.id) {
        mediaId = mediaUploadRes.data.id;
      } else {
        const errorMsg = mediaUploadRes?.errMsg || mediaUploadRes?.data?.error || 'Failed to register media with WhatsApp';
        yield put(showAlert({ message: `Media upload failed: ${errorMsg}`, type: 'error' }));
        yield put(sendMessageError(errorMsg));
        return;
      }

      // Step 3: Replace the header component with real media ID
      const components = [...finalContentData.template.components];
      const headerIndex = components.findIndex(c => c.type === 'header');
      if (headerIndex !== -1 && components[headerIndex].parameters?.length > 0) {
        const format = components[headerIndex].parameters[0].type; // 'image', 'video', 'document'
        components[headerIndex] = {
          ...components[headerIndex],
          parameters: [
            {
              type: format,
              [format]: { id: mediaId, filename: actualFile.name }
            }
          ]
        };
      }

      finalContentData.template = {
        ...finalContentData.template,
        components
      };
      // Clean up non-API fields
      delete finalContentData.template.mediaFile;
      delete finalContentData.template.companyId;
    }

    // Send message via HTTP API
    const res = yield call(callWithAuth, sendMessageApi, {
      conversationId,
      contentType,
      contentData: finalContentData,
    });

    if (res?.status === true) {
      const message = transformMessage({
        messageId: res.data?.messageId || `m-${Date.now()}`,
        authorType: 'AGENT',
        status: res.data?.status || 'QUEUED',
        createdAt: Date.now(),
        msgType: contentType,
        content:
          contentType === 'TEMPLATE'
            ? { type: 'TEMPLATE', template: finalContentData.template }
            : { type: contentType, ...finalContentData },
        direction: 'OUTBOUND',
        conversationId,
        ...messageMetadata,
      });

      yield put(sendMessageSuccess(message));
    } else {
      const errorMessage = res?.errMsg || 'Failed to send message';
      yield put(sendMessageError(errorMessage));
      yield put(
        showAlert({
          message: errorMessage,
          type: 'error',
        })
      );
    }
  } catch (error) {
    console.error('Error sending message:', error);
    const errorMessage = error?.message || 'Error sending message';
    yield put(sendMessageError(errorMessage));
    yield put(
      showAlert({
        message: errorMessage,
        type: 'error',
      })
    );
  }
}

function* handleCreateConversation(action) {
  if (!(yield call(ensureWhatsAppModuleAccess))) {
    return;
  }

  try {
    const { phoneNumber, channelId, customerName } = action.payload || {};

    if (!phoneNumber) {
      yield put(createConversationError('To Phone Number is required'));
      yield put(showAlert({ message: 'To Phone Number is required', type: 'error' }));
      return;
    }
    if (!channelId) {
      yield put(createConversationError('Send From number is required'));
      yield put(showAlert({ message: 'Send From number is required', type: 'error' }));
      return;
    }

    const res = yield call(callWithAuth, createConversationApi, {
      operation: 'CREATE_CONVERSATION',
      payload: {
        phoneNumber,
        channelId,
        ...(customerName ? { customerName } : {}),
      },
    });

    if (res?.status === true && res?.data?.conversationId) {
      const { conversationId, alreadyExisted } = res.data;
      yield put(createConversationSuccess(res.data));
      
      // Refresh list so the new/existing conversation shows up
      yield put(fetchConversationsRequest({ status: 'OPEN', limit: 10 }));

      // Fetch conversation messages and select it
      yield put(fetchConversationRequest({ conversationId }));

      yield put(
        showAlert({
          message: alreadyExisted 
            ? 'Conversation already existed' 
            : 'Conversation created successfully',
          type: 'success',
        })
      );
    } else {
      const errorMessage = res?.errMsg || 'Failed to create conversation';
      yield put(createConversationError(errorMessage));
      yield put(showAlert({ message: errorMessage, type: 'error' }));
    }
  } catch (error) {
    console.error('Error creating conversation:', error);
    const errorMessage = error?.message || 'Error creating conversation';
    yield put(createConversationError(errorMessage));
    yield put(showAlert({ message: errorMessage, type: 'error' }));
  }
}

function* handleCloseConversation(action) {
  if (!(yield call(ensureWhatsAppModuleAccess))) {
    return;
  }

  try {
    const { conversationId } = action.payload || {};

    if (!conversationId) {
      yield put(closeConversationError('Conversation ID is required'));
      yield put(showAlert({ message: 'Invalid conversation ID', type: 'error' }));
      return;
    }

    const res = yield call(callWithAuth, closeConversationApi, { conversationId });

    if (res?.status === true) {
      yield put(closeConversationSuccess({ conversationId }));
      yield put(
        showAlert({
          message: 'Conversation resolved successfully',
          type: 'success',
        })
      );
    } else {
      const errorMessage = res?.errMsg || 'Failed to resolve conversation';
      yield put(closeConversationError(errorMessage));
      yield put(
        showAlert({
          message: errorMessage,
          type: 'error',
        })
      );
    }
  } catch (error) {
    console.error('Error resolving conversation:', error);
    const errorMessage = error?.message || 'Error resolving conversation';
    yield put(closeConversationError(errorMessage));
    yield put(
      showAlert({
        message: errorMessage,
        type: 'error',
      })
    );
  }
}

export default function* conversationSaga() {
  yield takeLatest('conversation/fetchConversationsRequest', handleFetchConversations);
  yield takeLatest('conversation/fetchConversationRequest', handleFetchConversation);
  yield takeLatest('conversation/sendMessageRequest', handleSendMessage);
  yield takeLatest('conversation/createConversationRequest', handleCreateConversation);
  yield takeLatest('conversation/closeConversationRequest', handleCloseConversation);
  yield takeLatest('FETCH_CONVERSATION', handleFetchConversation);
}
